import { useState, useMemo, useCallback } from "react";
import { Link } from "@remix-run/react";
import type { MetaFunction } from "@remix-run/node";
import { motion, AnimatePresence } from "framer-motion";
import { FaRedo, FaCheck } from 'react-icons/fa';
import { HiOutlineShare, HiOutlineClipboardCopy } from 'react-icons/hi';
import { adhkarSEOConfigs } from "~/shared/utils/adhkar-seo";
import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";

// Styles
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/features/adhkar/uthmanic-fonts.css";
import "~/features/adhkar/adhkar.css";
import "~/features/adhkar/enhanced-cards.css";
import "~/features/adhkar/seo-enhancements.css";
// ============================================================================
// ADHKAR ROUTE - ENHANCED VERSION
// ============================================================================
// 
// يستخدم البنية الجديدة المحسنة مع الحفاظ على التوافق
// 
// ============================================================================

// Data imports
import { adhkarList } from "~/features/adhkar/data/adhkar-data";

// Font loader
import "~/features/adhkar/font-loader.js";

// ============================================================================
// SEO METADATA - تحسين محركات البحث
// ============================================================================

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('adhkar');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('adhkar');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "أذكاري اليومية - أسامة الزبيدي | أذكار الصباح والمساء والنوم بدون إعلانات | Daily Adhkar - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "أذكاري اليومية | أذكار الصباح والمساء والنوم بدون إعلانات" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/adhkar" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "أذكاري اليومية - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "أذكاري اليومية | أذكار الصباح والمساء والنوم بدون إعلانات" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "أذكاري اليومية - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/adhkar" },
    { name: "alternate", content: "https://oz.sa/adhkar", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/adhkar", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/adhkar", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// =====================
// Types
// =====================
type Lang = 'ar' | 'en';

// =====================
// UI Translations
// =====================
const translations: Record<Lang, Record<string, string>> = {
  ar: {
    title: "أذكاري",
    subtitle: "استعرض أذكارك اليومية بسهولة وجمال",
    switchLang: "English",
    back: "العودة للصفحة الرئيسية",
    backToSections: "العودة للأقسام",
    repeat: "فائدة",
    selectSection: "اختر قسم الأذكار",
    remaining: "متبقي",
    completed: "مكتمل",
    reset: "إعادة تعيين",
    copy: "نسخ الذكر",
    share: "مشاركة الذكر",
    copied: "تم النسخ!",
    shared: "تم المشاركة!",
    quran: "آية قرآنية",
    dua: "دعاء"
  },
  en: {
    title: "Adhkar",
    subtitle: "Browse your daily Azkar easily and beautifully",
    switchLang: "العربية",
    back: "Back to Home",
    backToSections: "Back to Sections",
    repeat: "Repeat",
    selectSection: "Select Adhkar Section",
    remaining: "Remaining",
    completed: "Completed",
    reset: "Reset",
    copy: "Copy Dhikr",
    share: "Share Dhikr",
    copied: "Copied!",
    shared: "Shared!",
    quran: "Quran Verse",
    dua: "Supplication"
  }
};

export default function AdhkarPage() {
  const [lang, setLang] = useState<Lang>('ar');
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showSectionSelector, setShowSectionSelector] = useState(true);
  const [counters, setCounters] = useState<Record<string, number>>({});
  const [notification, setNotification] = useState<{ message: string; type: 'copy' | 'share' } | null>(null);
  const t = translations[lang];

  const section = useMemo(() => adhkarList.find(s => s.id === selectedSection), [selectedSection]);

  const handleDhikrClick = useCallback((itemIndex: number) => {
    if (!section) return;
    
    const item = section.items[itemIndex];
    const counterKey = `${section.id}-${itemIndex}`;
    const currentCount = counters[counterKey] !== undefined ? counters[counterKey] : item.count;
    
    if (currentCount > 0) {
      setCounters(prev => ({
        ...prev,
        [counterKey]: currentCount - 1
      }));
    }
  }, [section, counters]);

  const resetCounter = useCallback((itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!section) return;
    
    const counterKey = `${section.id}-${itemIndex}`;
    const item = section.items[itemIndex];
    
    setCounters(prev => ({
      ...prev,
      [counterKey]: item.count
    }));
  }, [section]);

  const copyDhikr = useCallback(async (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!section) return;
    
    const item = section.items[itemIndex];
    const textToCopy = `${item.text[lang]}\n\n${t.repeat}: ${item.repeat[lang]}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setNotification({ message: t.copied, type: 'copy' });
      setTimeout(() => setNotification(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [section, lang, t]);

  const shareDhikr = useCallback(async (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!section) return;
    
    const item = section.items[itemIndex];
    
    // بناء النص للمشاركة مع جميع التفاصيل
    let textToShare = '';
    
    // إضافة البسملة للآيات القرآنية
    if (item.type === 'quran' && item.basmala) {
      textToShare += `${item.basmala[lang]}\n\n`;
    }
    
    // إضافة الاستعاذة للآيات القرآنية
    if (item.type === 'quran' && item.istiatha) {
      textToShare += `${item.istiatha[lang]}\n\n`;
    }
    
    // النص الرئيسي
    textToShare += `${item.text[lang]}\n\n`;
    
    // عدد التكرار
    textToShare += `${t.repeat}: ${item.repeat[lang]}\n`;
    
    // المرجع إن وجد
    if (item.reference) {
      textToShare += `${lang === 'ar' ? 'المرجع' : 'Reference'}: ${item.reference[lang]}\n`;
    }
    
    // اسم القسم والتطبيق
    textToShare += `\n📿 ${section.category[lang]} - ${t.title}`;
    
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ text: textToShare })) {
        await navigator.share({
          title: `${item.reference ? item.reference[lang] + ' - ' : ''}${section.category[lang]}`,
          text: textToShare,
          url: window.location.href
        });
        setNotification({ message: t.shared, type: 'share' });
        setTimeout(() => setNotification(null), 3000);
      } else {
        // Fallback to copy if sharing is not supported
        await navigator.clipboard.writeText(textToShare);
        setNotification({ message: t.copied, type: 'copy' });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (err) {
      console.error('Failed to share: ', err);
      // Fallback to copy on error
      try {
        await navigator.clipboard.writeText(textToShare);
        setNotification({ message: t.copied, type: 'copy' });
        setTimeout(() => setNotification(null), 3000);
      } catch (copyErr) {
        console.error('Failed to copy as fallback: ', copyErr);
        setNotification({ 
          message: lang === 'ar' ? 'حدث خطأ في المشاركة' : 'Share failed', 
          type: 'copy' 
        });
        setTimeout(() => setNotification(null), 3000);
      }
    }
  }, [section, lang, t]);

  const getCounterDisplay = (itemIndex: number) => {
    if (!section) return null;
    
    const item = section.items[itemIndex];
    const counterKey = `${section.id}-${itemIndex}`;
    const currentCount = counters[counterKey] !== undefined ? counters[counterKey] : item.count;
    
    return {
      current: currentCount,
      total: item.count,
      isCompleted: currentCount === 0
    };
  };

  return (
    <div className="adhkar-app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header with Glass Effect */}
      <div className="adhkar-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Dynamic Back Button */}
            {selectedSection ? (
              <button
                onClick={() => {
                  setSelectedSection(null);
                  setShowSectionSelector(true);
                }}
                className="adhkar-btn flex items-center gap-2"
                aria-label={lang === 'ar' ? "العودة للأقسام" : "Back to Sections"}
              >
                {lang === 'ar' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19l7-7-7-7" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {lang === 'ar' ? 'العودة للأقسام' : 'Back to Sections'}
                </span>
              </button>
            ) : (
              <Link
                to="/"
                className="adhkar-btn flex items-center gap-2"
                aria-label={lang === 'ar' ? "العودة للصفحة الرئيسية" : "Back to Home"}
              >
                {lang === 'ar' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {lang === 'ar' ? 'الصفحة الرئيسية' : 'Home'}
                </span>
              </Link>
            )}
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
                className="lang-switcher"
                aria-label={lang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
              >
                {lang === 'ar' ? 'EN' : 'عربي'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Animated Background */}
      <div className="relative flex flex-col items-center justify-center min-h-[50vh] md:min-h-[60vh] w-full bg-gradient-to-br from-purple-900 via-pink-800 to-purple-600 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Top gradient transition */}
          <div className="absolute top-0 left-0 w-full h-[10%] bg-gradient-to-b from-white/90 via-purple-50/80 to-transparent dark:from-gray-800 dark:via-purple-900/80 dark:to-transparent pointer-events-none" />
          
          {/* Floating orbs */}
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-10 md:top-20 left-10 md:left-20 w-20 h-20 md:w-32 md:h-32 bg-purple-400/30 rounded-full blur-xl"
          />
          <motion.div
            animate={{
              x: [0, -80, 0],
              y: [0, 60, 0],
              scale: [1, 0.8, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute top-20 md:top-40 right-16 md:right-32 w-16 h-16 md:w-24 md:h-24 bg-pink-400/40 rounded-full blur-xl"
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/20" />
          
          {/* Bottom gradient transition */}
          <div className="absolute bottom-0 left-0 w-full h-[10%] bg-gradient-to-t from-white/90 via-purple-50/80 to-transparent dark:from-gray-800 dark:via-purple-900/80 dark:to-transparent pointer-events-none" />
        </div>

        {/* Decorative Islamic Symbols */}
        <motion.span 
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute left-4 md:left-10 top-20 md:top-32 text-white/60 text-2xl md:text-4xl select-none z-10"
        >
          ☪
        </motion.span>
        <motion.span 
          animate={{ 
            rotate: [360, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute right-4 md:right-10 top-20 md:top-32 text-white/60 text-2xl md:text-4xl select-none z-10"
        >
          ✿
        </motion.span>

        {/* Main Hero Content */}
        <div className="relative z-10 text-center px-4 pt-16 md:pt-20 pb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white drop-shadow-2xl leading-tight mb-4 md:mb-6"
          >
            {t.title}
            <br />
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="block mt-2 md:mt-4 text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-purple-200"
            >
              {t.subtitle}
            </motion.span>
          </motion.h1>
          
          {/* Spiritual Quote */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-center text-purple-100 text-base sm:text-lg md:text-xl lg:text-2xl mt-4 md:mt-6 font-medium drop-shadow-lg max-w-3xl mx-auto"
          >
            &ldquo;أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ&rdquo;
            <br />
            <span className="text-purple-200 text-sm sm:text-base md:text-lg lg:text-xl">
              {lang === 'ar' ? 'ألا بذكر الله تطمئن القلوب' : 'Unquestionably, by the remembrance of Allah hearts are assured'}
            </span>
          </motion.p>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="relative bg-gradient-to-b from-white/90 via-purple-50/80 to-white dark:from-gray-800 dark:via-purple-900/80 dark:to-gray-800">
        <main className="max-w-4xl mx-auto px-4 py-6 md:py-8 -mt-4 md:-mt-8 relative z-10">
          {/* Section Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8 md:mb-12"
          >
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="adhkar-section-title text-xl md:text-2xl font-bold text-gray-800 dark:text-white text-center flex-1">
                {showSectionSelector ? t.selectSection : (lang === 'ar' ? 'القسم المختار' : 'Selected Section')}
              </h2>
              {!showSectionSelector && selectedSection && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setShowSectionSelector(true)}
                  className="bg-purple-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 md:gap-2 shadow-lg hover:shadow-xl text-sm md:text-base"
                >
                  {lang === 'ar' ? (
                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19l7-7-7-7" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">{lang === 'ar' ? 'العودة للأقسام' : 'Back to Sections'}</span>
                  <span className="sm:hidden">{lang === 'ar' ? 'العودة' : 'Back'}</span>
                </motion.button>
              )}
            </div>
            
            <AnimatePresence mode="wait">
              {showSectionSelector ? (
                <motion.div
                  key="section-grid"
                  initial={{ opacity: 0, height: 0, scale: 0.9 }}
                  animate={{ opacity: 1, height: 'auto', scale: 1 }}
                  exit={{ opacity: 0, height: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="adhkar-sections-container">
                    <div className="adhkar-sections-grid">
                      {adhkarList.map((section, index) => (
                        <motion.div
                          key={section.id}
                          initial={{ opacity: 0, y: 30, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ 
                            duration: 0.6, 
                            delay: index * 0.1, 
                            ease: [0.4, 0, 0.2, 1] 
                          }}
                          className={`adhkar-section-card-enhanced ${
                            selectedSection === section.id ? 'selected-card' : ''
                          }`}
                          whileHover={{ 
                            y: -8,
                            scale: 1.02,
                            transition: { 
                              duration: 0.3, 
                              ease: [0.4, 0, 0.2, 1] 
                            }
                          }}
                          whileTap={{ 
                            scale: 0.98,
                            transition: { duration: 0.15 }
                          }}
                          layout
                        >
                          <Link
                            to={`/adhkar/${adhkarSEOConfigs.find(config => config.id === section.id)?.slug || section.id}`}
                            className="adhkar-card-link"
                            onClick={() => {
                              setSelectedSection(section.id);
                              setShowSectionSelector(false);
                            }}
                          >
                            {/* Enhanced Icon Container */}
                            <div className="adhkar-icon-wrapper">
                              <div className={`adhkar-icon-container ${section.color}`}>
                                <div className="adhkar-icon-bg"></div>
                                <span className="adhkar-icon">{section.icon}</span>
                              </div>
                            </div>

                            {/* Card Content */}
                            <div className="adhkar-card-content">
                              <h3 className="adhkar-card-title">
                                {section.category[lang]}
                              </h3>
                              
                              <p className="adhkar-card-description">
                                {section.description[lang]}
                              </p>

                              {/* Enhanced Count Display */}
                              <div className="adhkar-count-display">
                                <span className="adhkar-count-label">
                                  {lang === 'ar' ? 'عدد الأذكار' : 'Count'}
                                </span>
                                <div className="adhkar-count-badge">
                                  <span className="adhkar-count-number">{section.items.length}</span>
                                  <div className="adhkar-count-bg"></div>
                                </div>
                              </div>

                              {/* Selection Indicator */}
                              {selectedSection === section.id && (
                                <motion.div 
                                  initial={{ opacity: 0, scale: 0.8, y: 10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  transition={{ 
                                    duration: 0.4, 
                                    ease: [0.68, -0.55, 0.265, 1.55] 
                                  }}
                                  className="adhkar-selected-indicator"
                                >
                                  <div className="adhkar-selected-content">
                                    <svg className="adhkar-check-icon" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="adhkar-selected-text">
                                      {lang === 'ar' ? 'محدد' : 'Selected'}
                                    </span>
                                  </div>
                                  <div className="adhkar-selected-glow"></div>
                                </motion.div>
                              )}
                            </div>

                            {/* Card Decoration */}
                            <div className="adhkar-card-decoration">
                              <div className="adhkar-decoration-1"></div>
                              <div className="adhkar-decoration-2"></div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ) : selectedSection && section ? (
                <motion.div
                  key="selected-section"
                  initial={{ opacity: 0, y: -20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.9 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 max-w-md mx-auto border-2 border-purple-200 dark:border-purple-700 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-full -translate-y-10 translate-x-10 opacity-50"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full translate-y-8 -translate-x-8 opacity-50"></div>
                    
                    <div className="relative z-10">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${section.color} flex items-center justify-center text-3xl shadow-lg`}>
                        {section.icon}
                      </div>
                      <h3 className="adhkar-card-heading font-bold text-gray-800 dark:text-white mb-2">
                        {section.category[lang]}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {section.description[lang]}
                      </p>
                      <div className="flex items-center justify-center gap-4 text-sm mb-3">
                        <span className="text-gray-500 dark:text-gray-400">
                          {lang === 'ar' ? 'عدد الأذكار' : 'Azkar Count'}
                        </span>
                        <span className="font-medium text-purple-600 dark:text-purple-400">
                          {section.items.length}
                        </span>
                      </div>
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="text-xs text-green-700 dark:text-green-300">
                            {lang === 'ar' 
                              ? 'تم اختيار القسم بنجاح! يمكنك الآن قراءة أذكارك.'
                              : 'Section selected successfully! You can now read your Azkar.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>

          {/* Display Azkar - Only show if section is selected and selector is hidden */}
          {selectedSection && section && !showSectionSelector && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-bold text-purple-700 dark:text-purple-300">{section.category[lang]}</h2>
                <button
                  onClick={() => setShowSectionSelector(true)}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 md:px-3 py-1 rounded-lg text-xs md:text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {lang === 'ar' ? 'تغيير' : 'Change'}
                </button>
              </div>
              
              <ul className="space-y-4 md:space-y-6">
                {section.items.map((item, i) => {
                  const counter = getCounterDisplay(i);
                  return (
                    <motion.div 
                      key={`${section.id}-${i}`} 
                      onClick={() => !counter?.isCompleted && handleDhikrClick(i)}
                      className={`adhkar-card cursor-pointer ${
                        counter?.isCompleted 
                          ? 'border-green-300 bg-green-50 dark:bg-green-900/10 dark:border-green-700' 
                          : 'border-transparent'
                      } ${item.type === 'quran' ? 'quran-card' : 'dua-card'}`}
                      whileHover={!counter?.isCompleted ? { 
                        scale: 1.02, 
                        y: -2,
                        transition: { duration: 0.3, ease: "easeOut" }
                      } : {}}
                      whileTap={!counter?.isCompleted ? { 
                        scale: 0.98,
                        transition: { duration: 0.1 }
                      } : {}}
                      layout
                    >
                      {/* Card Header with Type Badge */}
                      <div className="relative p-5 md:p-6">
                        {/* Type Badge */}
                        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
                          item.type === 'quran' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        }`}>
                          {item.type === 'quran' ? t.quran : t.dua}
                        </div>
                        
                        {/* Reference Badge */}
                        {item.reference && (
                          <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
                            {item.reference[lang]}
                          </div>
                        )}
                        
                        {/* Decorative corner elements */}
                        <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-bl from-purple-100 to-transparent dark:from-purple-900/20 rounded-bl-full"></div>
                        <div className="absolute bottom-0 left-0 w-6 h-6 bg-gradient-to-tr from-pink-100 to-transparent dark:from-pink-900/20 rounded-tr-full"></div>
                        
                        {/* Main content */}
                        <div className="relative z-10 mt-6">
                          {/* Basmala for Quranic Surahs */}
                          {item.type === 'quran' && item.basmala && (
                            <div className="text-center mb-4">
                              <p className="quran-text basmala">
                                {item.basmala[lang]}
                              </p>
                            </div>
                          )}
                          
                          {/* Istiaza for Quranic Verses */}
                          {item.type === 'quran' && item.istiatha && (
                            <div className="text-center mb-3">
                              <p className="quran-text istiaza">
                                {item.istiatha[lang]}
                              </p>
                            </div>
                          )}
                          
                          {/* Main text */}
                          <p className={`adhkar-item-text text-base md:text-lg font-semibold text-gray-800 dark:text-white leading-relaxed mb-3 ${
                            item.type === 'quran' ? 'quran-text' : 'dua-text'
                          }`}>
                            {item.text[lang]}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 font-medium">
                              {t.repeat}: {item.repeat[lang]}
                            </span>
                            {/* Progress indicator */}
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                              <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                              <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                                             {/* Enhanced Counter Section */}
                       <div className={`adhkar-counter w-full h-16 sm:h-18 md:h-22 flex items-center justify-center relative overflow-hidden ${
                        item.type === 'quran' ? 'quran-counter' : 'dua-counter'
                      }`}>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-5">
                          <div className="w-full h-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                        </div>
                        
                                                 {/* Counter Display */}
                         <div 
                           className={`font-bold text-2xl sm:text-3xl md:text-4xl transition-all duration-500 relative z-10 ${
                            counter?.isCompleted 
                              ? 'text-green-600 dark:text-green-400' 
                              : item.type === 'quran' 
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-purple-600 dark:text-purple-400'
                          }`}
                        >
                          {counter?.isCompleted ? (
                            <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                              <FaCheck className="text-xl sm:text-2xl md:text-3xl" />
                              <span className="text-sm sm:text-base md:text-lg font-medium">
                                {lang === 'ar' ? 'مكتمل' : 'Done'}
                              </span>
                            </div>
                          ) : (
                            <div className="text-center">
                              <div className="text-2xl sm:text-3xl md:text-4xl font-black">
                                {counter?.current}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
                                {lang === 'ar' ? 'متبقي' : 'Remaining'}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Simple Action Buttons */}
                        <div className="absolute right-2 sm:right-3 md:right-4 flex items-center gap-1.5 sm:gap-2">
                          {/* Copy Button */}
                          <button
                            onClick={(e) => copyDhikr(i, e)}
                            className={`p-2 sm:p-2.5 rounded-lg transition-colors duration-200 ${
                              counter?.isCompleted 
                                ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                                : 'bg-white text-purple-600 hover:bg-purple-50 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-purple-900/20'
                            } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                            title={t.copy}
                          >
                            <HiOutlineClipboardCopy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          
                          {/* Share Button */}
                          <button
                            onClick={(e) => shareDhikr(i, e)}
                            className={`p-2 sm:p-2.5 rounded-lg transition-colors duration-200 ${
                              counter?.isCompleted 
                                ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                                : 'bg-white text-blue-600 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-blue-900/20'
                            } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                            title={t.share}
                          >
                            <HiOutlineShare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          
                          {/* Reset Button */}
                          <button
                            onClick={(e) => resetCounter(i, e)}
                            className={`p-2 sm:p-2.5 rounded-lg transition-colors duration-200 ${
                              counter?.isCompleted 
                                ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                                : 'bg-white text-orange-600 hover:bg-orange-50 dark:bg-gray-800 dark:text-orange-400 dark:hover:bg-orange-900/20'
                            } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                            title={t.reset}
                          >
                            <FaRedo className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </main>
      </div>

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg border ${
              notification.type === 'copy' 
                ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
                : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700'
            }`}
          >
            <div className="flex items-center gap-2">
              {notification.type === 'copy' ? (
                <HiOutlineClipboardCopy className="w-4 h-4" />
              ) : (
                <HiOutlineShare className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
